import { Certification } from '../../../types/certifications/Certification';

export type MonitoringRuntimeConfig = {
  livenessChallengeTimeoutSeconds: number;
  monitoringIncidentCorrectionWindowSeconds: number;
  monitoringFocusLossLimit: number;
  monitoringQuickCheckIntervalSeconds: number;
  webcamCaptureDurationSeconds: number;
  webcamCaptureIntervalMinutes: number;
  environmentCheckTimeoutMs: number;
  monitoringCheckSecondScreenEnabled: boolean;
  monitoringCheckVirtualWebcamEnabled: boolean;
  monitoringCheckSuspiciousExtensionsEnabled: boolean;
  screenShareEnabled: boolean;
  screenShareCaptureIntervalSeconds: number;
  screenShareIncidentCorrectionWindowSeconds: number;
  screenShareCaptureQuality: number;
};

export const DEFAULT_MONITORING_RUNTIME_CONFIG: MonitoringRuntimeConfig = {
  livenessChallengeTimeoutSeconds: 15,
  monitoringIncidentCorrectionWindowSeconds: 45,
  monitoringFocusLossLimit: 5,
  monitoringQuickCheckIntervalSeconds: 15,
  webcamCaptureDurationSeconds: 15,
  webcamCaptureIntervalMinutes: 2,
  environmentCheckTimeoutMs: 10000,
  monitoringCheckSecondScreenEnabled: false,
  monitoringCheckVirtualWebcamEnabled: false,
  monitoringCheckSuspiciousExtensionsEnabled: false,
  screenShareEnabled: false,
  screenShareCaptureIntervalSeconds: 60,
  screenShareIncidentCorrectionWindowSeconds: 15,
  screenShareCaptureQuality: 0.7,
};

function parseBooleanWithDefault(value: unknown, fallback: boolean): boolean {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'boolean') return value;
  return fallback;
}

function parsePositiveNumber(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsedValue =
    typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return undefined;
  }

  return parsedValue;
}

function parsePositiveInteger(value: unknown) {
  const parsedValue = parsePositiveNumber(value);
  if (parsedValue === undefined) return undefined;

  return Math.round(parsedValue);
}

export function resolveMonitoringRuntimeConfig(
  certification?: Certification | null,
): MonitoringRuntimeConfig {
  if (!certification) {
    return DEFAULT_MONITORING_RUNTIME_CONFIG;
  }

  return {
    livenessChallengeTimeoutSeconds:
      parsePositiveInteger(certification.liveness_challenge_timeout_seconds) ??
      DEFAULT_MONITORING_RUNTIME_CONFIG.livenessChallengeTimeoutSeconds,
    monitoringIncidentCorrectionWindowSeconds:
      parsePositiveInteger(
        certification.monitoring_incident_correction_window_seconds,
      ) ??
      DEFAULT_MONITORING_RUNTIME_CONFIG.monitoringIncidentCorrectionWindowSeconds,
    monitoringFocusLossLimit:
      parsePositiveInteger(certification.monitoring_focus_loss_limit) ??
      DEFAULT_MONITORING_RUNTIME_CONFIG.monitoringFocusLossLimit,
    monitoringQuickCheckIntervalSeconds:
      parsePositiveInteger(
        certification.monitoring_quick_check_interval_seconds,
      ) ??
      DEFAULT_MONITORING_RUNTIME_CONFIG.monitoringQuickCheckIntervalSeconds,
    webcamCaptureDurationSeconds:
      parsePositiveInteger(certification.webcam_capture_duration_seconds) ??
      DEFAULT_MONITORING_RUNTIME_CONFIG.webcamCaptureDurationSeconds,
    webcamCaptureIntervalMinutes:
      parsePositiveNumber(certification.webcam_capture_interval_minutes) ??
      DEFAULT_MONITORING_RUNTIME_CONFIG.webcamCaptureIntervalMinutes,
    environmentCheckTimeoutMs:
      parsePositiveInteger(certification.environment_check_timeout_ms) ??
      DEFAULT_MONITORING_RUNTIME_CONFIG.environmentCheckTimeoutMs,
    monitoringCheckSecondScreenEnabled: parseBooleanWithDefault(
      certification.monitoring_check_second_screen_enabled,
      DEFAULT_MONITORING_RUNTIME_CONFIG.monitoringCheckSecondScreenEnabled,
    ),
    monitoringCheckVirtualWebcamEnabled: parseBooleanWithDefault(
      certification.monitoring_check_virtual_webcam_enabled,
      DEFAULT_MONITORING_RUNTIME_CONFIG.monitoringCheckVirtualWebcamEnabled,
    ),
    monitoringCheckSuspiciousExtensionsEnabled: parseBooleanWithDefault(
      certification.monitoring_check_suspicious_extensions_enabled,
      DEFAULT_MONITORING_RUNTIME_CONFIG.monitoringCheckSuspiciousExtensionsEnabled,
    ),
    screenShareEnabled: parseBooleanWithDefault(
      certification.screen_share_enabled,
      DEFAULT_MONITORING_RUNTIME_CONFIG.screenShareEnabled,
    ),
    screenShareCaptureIntervalSeconds:
      parsePositiveInteger(
        certification.screen_share_capture_interval_seconds,
      ) ?? DEFAULT_MONITORING_RUNTIME_CONFIG.screenShareCaptureIntervalSeconds,
    screenShareIncidentCorrectionWindowSeconds:
      parsePositiveInteger(
        certification.screen_share_incident_correction_window_seconds,
      ) ??
      DEFAULT_MONITORING_RUNTIME_CONFIG.screenShareIncidentCorrectionWindowSeconds,
    screenShareCaptureQuality:
      DEFAULT_MONITORING_RUNTIME_CONFIG.screenShareCaptureQuality,
  };
}
